/* eslint-disable max-statements */
import { Box, Button, Group, Loader, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconRefresh } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { useActiveSession, useRequests, useStartSession } from '@admin/hooks';
import { ContentLayout } from '@features/layout';
import { EmptySessionState } from './EmptySessionState';
import { RequestDetails } from './RequestDetails';
import { RequestLogsTab } from './RequestLogsTab';
import { SessionDetailsCard } from './SessionDetailsCard';
import { SessionStatsCards } from './SessionStatsCards';

export const RequestLogger = () => {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchQuery, 300);

  const { data: sessionData, isLoading: isSessionLoading } = useActiveSession();
  const { data: { data: requests = [], total = 0 } = {}, isLoading: isRequestsLoading } = useRequests({
    page,
    size,
    sortField: 'createdAt',
    sortDirection: 'desc',
    urlPrefix: debouncedSearch || undefined,
  });

  const isSessionActive = sessionData?.session !== null;

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const startSessionMutation = useStartSession();

  const handleStartSession = () => {
    startSessionMutation.mutate();
  };

  const handlePagination = (newPage: number, newSize: number) => {
    setPage(newPage);
    setSize(newSize);
  };

  if (isSessionLoading) {
    return (
      <ContentLayout>
        <ContentLayout.Header>
          <Title order={2}>Request Observability</Title>
        </ContentLayout.Header>
        <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <Loader size="xl" />
        </Box>
      </ContentLayout>
    );
  }

  const sessionDurationMinutes = sessionData?.duration ? Math.floor(sessionData.duration / 60000) : 0;

  const openRequestDetails = (requestId: string) => {
    modals.open({
      title: 'Request Details',
      size: 'xl',
      centered: true,
      children: <RequestDetails requestId={requestId} onClose={() => modals.closeAll()} />,
    });
  };

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <Group justify="space-between" style={{ width: '100%' }}>
          <Stack gap="xs">
            <Title order={2}>Request Observability</Title>
            <Text size="sm" c="dimmed">
              Monitor and track all API requests and database changes in real-time
            </Text>
          </Stack>
          <Button
            onClick={handleStartSession}
            variant="gradient"
            gradient={isSessionActive ? { from: 'teal', to: 'cyan' } : { from: 'blue', to: 'cyan' }}
            size="lg"
            loading={startSessionMutation.isPending}
            leftSection={<IconRefresh size={18} />}
          >
            {isSessionActive ? 'Extend Session' : 'Start Session'}
          </Button>
        </Group>
      </ContentLayout.Header>

      <ScrollArea h="calc(100vh - 120px)" type="auto">
        <Box p="md">
          <Stack gap="lg">
            <SessionStatsCards isSessionActive={isSessionActive} sessionDurationMinutes={sessionDurationMinutes} requestsCount={sessionData?.requests || 0} />

            {isSessionActive && sessionData?.session && <SessionDetailsCard session={sessionData.session} duration={sessionData.duration} />}

            {!isSessionActive && <EmptySessionState onStartSession={handleStartSession} />}

            <RequestLogsTab
              requests={requests}
              total={total}
              isLoading={isRequestsLoading}
              searchQuery={searchQuery}
              page={page}
              size={size}
              onSearchChange={setSearchQuery}
              onPaginationChange={handlePagination}
              onRowClick={openRequestDetails}
            />
          </Stack>
        </Box>
      </ScrollArea>
    </ContentLayout>
  );
};
